import { useState, useRef, useEffect, useCallback } from 'react';
import images from 'assets/Images';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import './style.scss';
import Images from 'assets/Images';

export default function SelectMenu(props) {
	const [dropdownToggle, setDropdownToggle] = useState(false);
	const [selectedOptions, setSelectedOptions] = useState([]);
	const [dropdownOptions, setDropdownOptions] = useState([]);
	const dropdownOptionsRef = useRef();
	const dropdownDisplayRef = useRef();
	const { value, onChange, menuOptions, disabled, multiple, infoContent, placeholder, display, label, mandatory, last } = props;

	// dropdown toggler and animation state handler
	const dropdownToggler = e => {
		!disabled && setDropdownToggle(prevState => !prevState);
	};

	// outside click handle function
	const handleClickOutside = useCallback(
		event => {
			if (dropdownOptionsRef?.current && !dropdownOptionsRef?.current?.contains(event?.target) && !dropdownDisplayRef?.current?.contains(event?.target)) {
				dropdownToggle && dropdownToggler(false);
			}
		},
		[dropdownToggler, dropdownToggle]
	);

	// set menu options state
	useEffect(() => {
		setDropdownOptions(menuOptions);
	}, [menuOptions]);

	// two way binding
	useEffect(() => {
		value && setSelectedOptions(value);
	}, [value]);

	// outside click handler
	useEffect(() => {
		document.addEventListener('click', handleClickOutside, true);
		return () => {
			document.removeEventListener('click', handleClickOutside, true);
		};
	}, [dropdownToggle, handleClickOutside]);

	// dropdown select handler
	const selectHandler = e => {
		const options = multiple ? [...new Set([e, ...selectedOptions])] : [e];
		setSelectedOptions(options);
		onChange(multiple ? options?.map(el => el?.key) : options[0]?.key);
		if (!multiple) {
			dropdownToggler(false);
			setDropdownOptions(menuOptions);
		}
	};

	// remove selected options
	const removeOptions = e => {
		const data = selectedOptions?.filter(el => el.key !== e?.key);
		setSelectedOptions(data);
		onChange(multiple ? data?.map(el => el?.key) : data?.[0]?.key);
	};

	// dropdown option list
	const menuContent =
		dropdownOptions?.length > 0 ? (
			dropdownOptions?.map((el, i) => (
				<li key={i} onClick={() => selectHandler(el)}>
					{el?.value}
				</li>
			))
		) : (
			<span>No records found!</span>
		);

	// display content or placeholder on select
	const displayContent =
		selectedOptions?.length > 0 ? (
			<span>
				{selectedOptions[0]?.value} {selectedOptions?.length > 1 && `+${selectedOptions.length - 1}`}
			</span>
		) : (
			<span className='select__dropdown--placeholder'>{placeholder}</span>
		);

	const selectedContent = selectedOptions?.map((el, i) => (
		<span className='select__dropdown--selectedtab' onClick={() => removeOptions(el)} key={i}>
			{el?.value}
		</span>
	));

	// search functionality
	const searchValue = event => {
		const filteredData = menuOptions?.filter(items => items?.value?.toLowerCase()?.includes(event?.target?.value?.toLowerCase()));
		setDropdownOptions(filteredData);
	};

	const displayItems = (
		<div className='select__display'>
			<h5>{label}</h5>
			<span className='select__value'>{displayContent || '-'}</span>
		</div>
	);

	const dropdown = (
		<div className={classNames({ select: true, 'select--disabled': disabled, 'select--last': last })}>
			{label && (
				<label>
					{label} {mandatory && <sup>*</sup>}
					{infoContent && (
						<div className='textbox-row__helpContainer'>
							<div className='textbox-row__help'>
								<img src={Images.help} />
							</div>
							<div className='textbox-row__helpContent'>{infoContent}</div>
						</div>
					)}
				</label>
			)}
			<div ref={dropdownOptionsRef} className='select__dropdown'>
				<div ref={dropdownDisplayRef} className='select__dropdown--display' onClick={() => dropdownToggler(!dropdownToggle)}>
					{displayContent}
				</div>
				<img className='select__dropdown--icon' src={images.downArrow} onClick={() => dropdownToggler(!dropdownToggle)} alt='arrow' />

				<AnimatePresence>
					{dropdownToggle && (
						<motion.ul initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -10, opacity: 0 }} transition={{ duration: 0.2 }} className={classNames({ 'scroll-bar': true })}>
							<li>
								<input placeholder='Search' onChange={searchValue} />
								<div className='select__selected'>{selectedContent}</div>
							</li>
							{menuContent}
						</motion.ul>
					)}
				</AnimatePresence>
			</div>
		</div>
	);

	return <>{display ? displayItems : dropdown}</>;
}

SelectMenu.propTypes = {
	value: PropTypes.any,
	onChange: PropTypes.any,
	menuOptions: PropTypes.any,
	disabled: PropTypes.bool,
	multiple: PropTypes.bool,
	display: PropTypes.bool,
	last: PropTypes.bool,
	placeholder: PropTypes.string,
	label: PropTypes.string,
	mandatory: PropTypes.bool
};

SelectMenu.defaultProps = {
	value: '',
	onChange: '',
	menuOptions: '',
	disabled: false,
	multiple: false,
	display: false,
	placeholder: '',
	label: '',
	mandatory: false,
	last: false
};
